import { useEffect, useMemo, useRef } from 'react';
import { teamAbbrevLookup, type TeamAbbrev } from '../../core/model/teams';
import type { Game } from '../../core/model/types';
import { getRoundDate } from '../../core/time/roundDate';
import { leagueSettings, settings, type ScoreFilter } from '../../core/settings/settings';
import { useSetting } from '../../core/settings/useSetting';
import type { AppData } from '../appData';
import { useNow } from '../common/useNow';
import { isHiddenByTeam } from '../today/gameViewModel';
import { DEFAULT_ROUTE, type Route } from './useHashRoute';

// Whether the round's games could give something away on a tab other than
// Tänään: the scores are off, or a hidden team plays in the round. (A new round
// switches the team hiding on by itself, so whether it is on right now is moot.)
export function roundMaySpoil(
    scores: readonly Game[],
    roundDate: string,
    scoreFilter: ScoreFilter,
    hiddenTeams: readonly string[],
    teamAbbrev?: TeamAbbrev,
): boolean {
    return scoreFilter === 'none'
        || scores.some(game => game.date === roundDate && isHiddenByTeam(game, hiddenTeams, teamAbbrev));
}

// When the game day changes while another tab is open and the new round could
// spoil something there (the standings, the season stats, the upcoming games and
// so on), moves to Tänään, where the spoiler settings are respected. The first
// round seen after the data has loaded is not a change, so opening a link to
// another tab is left alone.
export function useRoundGuard(data: AppData | undefined, route: Route): void {
    const now = useNow();
    const scores = data?.scores;
    const roundDate = useMemo(() => scores && getRoundDate(now, scores), [now, scores]);
    const [scoreFilter] = useSetting(settings.scoreFilter);
    const [hiddenList] = useSetting(leagueSettings(data?.league ?? 'NHL').hideTeamsList);
    const lastRound = useRef<string>();

    useEffect(() => {
        if (data === undefined || roundDate === undefined || lastRound.current === roundDate) {
            return;
        }
        const changed = lastRound.current !== undefined;
        lastRound.current = roundDate;
        if (changed && route !== DEFAULT_ROUTE
            && roundMaySpoil(data.scores, roundDate, scoreFilter, hiddenList, teamAbbrevLookup(data.league, data.standings))) {
            // Replaces the entry, so that Back does not return to the tab that spoils.
            window.location.replace(`#${DEFAULT_ROUTE}`);
        }
    }, [data, roundDate, route, scoreFilter, hiddenList]);
}
